import React, { useEffect } from "react";
import LoadingManager, { LoadingManagerData } from "./LoadingManager";
import { Spinner, SpinnerSize } from "@fluentui/react";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import { formatMessageDefault } from "../../utils/helpers/LanguageHelper";
import { abortAbortableElaborationStatusItems } from "../../utils/helpers/ElaborationHelper";

export interface LoadingContainerState {
  requesters: Map<string, LoadingManagerData>;
}

const BASE_TIMEOUT: number = 0.1;

function calculateTimeout(numItems: number, dateCheck): number {
  const minutes = (BASE_TIMEOUT / Math.sqrt(numItems)) * numItems;
  const timeElapsed = getTimeElapsed(dateCheck);
  return minutes * 60 * 1000 - timeElapsed;
}

function getTimeElapsed(backendTimestamp: string): number {
  const backendDate = new Date(backendTimestamp + "Z");
  const currentDate = Date.now();
  return currentDate - backendDate.getTime();
}
const checkItems = (requestersValues: LoadingManagerData[]) => {
  let elaborationsCount = 0;
  let startedItemExists = false;
  let startElaboration = null;

  requestersValues.forEach((loadingManagerData) => {
    if (loadingManagerData?.isAbortable && loadingManagerData.items) {
      loadingManagerData.items.forEach((elaborationStatus) => {
        if (elaborationStatus.items) {
          elaborationsCount += elaborationStatus.items.length;
          if (!startedItemExists) {
            startedItemExists = elaborationStatus.items.some(
              (item) => item.status === "STARTED" || item.status === "REQUESTED"
            );
          }
          const itemWithStartDate = elaborationStatus.items.find((item) => item.startDate);
          if (itemWithStartDate) {
            startElaboration = itemWithStartDate.startDate;
          }
        }
      });
    }
  });

  return {
    elaborationsCount,
    startedItemExists,
    startElaboration,
  };
};

export const LoadingContainer: React.FunctionComponent = () => {
  const [state, setState] = React.useState<LoadingContainerState>({
    requesters: new Map<string, LoadingManagerData>(),
  });
  const [abortClicked, setAbortClicked] = React.useState<boolean>(false);
  const [showAbort, setShowAbort] = React.useState<boolean>(false);

  useEffect(() => {
    LoadingManager.addChangeListener(handleStoreChange);
    setAbortClicked(false);
    return () => {
      LoadingManager.removeChangeListener(handleStoreChange);
    };
  }, []);

  function handleStoreChange(requesters) {
    setState({
      requesters,
    });
  }

  const visible = state.requesters.size;
  const isAbortable = visible && Array.from(state.requesters.values()).some((value) => value?.isAbortable);
  const { elaborationsCount, startedItemExists, startElaboration } = checkItems(
    Array.from(state.requesters.values() ?? [])
  );

  const abort = () => {
    setAbortClicked(true);
    if (state.requesters.size > 0) {
      const items = Array.from(state.requesters.values())
        .filter((loadingManagerData) => loadingManagerData?.isAbortable)
        .flatMap((item) => item.items)
        .flatMap((item) => item.items)
        .filter((item) => !item.userChecked);
      abortAbortableElaborationStatusItems(items);
    }
  };

  useEffect(() => {
    setShowAbort(false);
    let timer = null;
    if (isAbortable && elaborationsCount > 0 && startedItemExists) {
      const timeoutMilliseconds = calculateTimeout(elaborationsCount, startElaboration);
      // eslint-disable-next-line no-undef
      timer = setTimeout(() => {
        // eslint-disable-next-line no-undef
        console.log(" ::: Abort enabled after " + timeoutMilliseconds + " milliseconds ::: ");
        setShowAbort(true);
        setAbortClicked(false);
      }, timeoutMilliseconds);
    }
    return () => {
      // eslint-disable-next-line no-undef
      clearTimeout(timer);
    };
  }, [isAbortable, elaborationsCount, startedItemExists]);

  if (visible)
    return (
      <div
        style={{
          width: "100vw",
          height: "100vh",
          position: "absolute",
          top: "0",
          left: "0",
          opacity: "0.8",
          zIndex: "1000001",
          backgroundColor: "gray",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            height: "100%",
            paddingTop: "60%",
          }}
        >
          <Spinner size={SpinnerSize.large} id="spinner" />
          {isAbortable && showAbort && startedItemExists ? (
            <>
              <div style={{ color: "white", marginBottom: "16px", marginTop: "20px" }}>
                {formatMessageDefault("cof.elaboration.abort.question", "Taking too much time?")}
              </div>
              <PrimaryButton
                style={{
                  backgroundColor: abortClicked ? "gray" : "var(--color-red)",
                  borderColor: abortClicked ? "var(--color-black)" : "var(--color-red)",
                }}
                text={formatMessageDefault("cof.base.abort", "Abort")}
                onClick={() => abort()}
                disabled={abortClicked}
              />
            </>
          ) : null}
        </div>
      </div>
    );
  return null;
};

export default LoadingContainer;
